import { useRef, useEffect, useState, useLayoutEffect } from 'react'
import SectionTitle from '@ui/section-title'
import { Container, Row, Col } from '@ui/wrapper'
import Tabs, { Tab } from '@ui/tabs'
import {
  SectionWrapper,
  StyledText,
  StyledText2,
  StyledTitle,
  SectionBody,
  ClassificationItem,
  TabsWrapper,
  CheckWrapper,
  Circle,
  CircleWrapper,
  SummaryCircleWrapper,
  SVGWrapper
} from './style'
import BoxImage from '@components/box-image/layout-10'
import { ProduktKlassifizierung } from './produktklassifizierung'
import Form from 'react-bootstrap/Form'
import { usePageContext } from '@context/PageContext'
import {
  texts,
  categoryInfoContentDict,
  categoryInfoColorDict,
  capitalProtect,
  investProductsWithoutCP,
  leverageProducts,
  checkCategoriesDict
} from '@constants/bsw-produktklassifizierung'

function intersect(a, b) {
  const setB = new Set(b)
  return [...new Set(a)].filter((x) => setB.has(x))
}

function getPosition(el) {
  if (!el) return 'middle'

  const windowHalfWidth = window.innerWidth / 2
  if (
    (el.offsetLeft < windowHalfWidth &&
      el.offsetLeft + el.offsetWidth > windowHalfWidth) ||
    (el.offsetLeft < windowHalfWidth &&
      el.offsetLeft + el.offsetWidth > windowHalfWidth - el.offsetWidth / 4) ||
    (el.offsetLeft < windowHalfWidth + el.offsetWidth / 4 &&
      el.offsetLeft + el.offsetWidth > windowHalfWidth)
  ) {
    return 'middle'
  }
  return el.offsetLeft + el.offsetWidth < windowHalfWidth ? 'left' : 'right'
}

const BSWProduktklassifizierungArea = ({
  data,
  einleitung,
  search,
  onSetSection,
  ...rest
}) => {
  const refSummary = useRef(null)
  const refSearch = useRef(null)
  const refSearch2 = useRef(null)
  const handleSetSection = onSetSection

  const circleRefsSummary = useRef({})
  const circleRefsSearch = useRef({})

  const [circlePositions, setCirclePositions] = useState({})
  const [openTab, setOpenTab] = useState('summary')
  const [categorySummaryTab, setCategorySummaryTab] = useState(null)
  const [categorySearchTab, setCategorySearchTab] = useState(null)
  const [categoryList, setCategoryList] = useState([])
  const [categoryListKeyAddition, setCategoryListKeyAddition] = useState(0)
  const [resize, setResize] = useState(0)
  const [summaryCirclesVisible, setSummaryCirclesVisible] = useState(false)

  const { locale } = usePageContext()

  const localeTexts = texts[locale] || texts.de
  const localeCategoryInfoContentDict =
    categoryInfoContentDict[locale] || categoryInfoContentDict.de

  useEffect(() => {
    data?.section_title?.title &&
      refSummary.current?.scrollIntoView({ behavior: 'smooth' })
  }, [categorySummaryTab])

  useEffect(() => {
    data?.section_title?.title &&
      refSearch.current?.scrollIntoView({ behavior: 'smooth' })
  }, [categorySearchTab])

  useEffect(() => {
    einleitung?.texts[1]?.content &&
      refSearch2.current?.scrollIntoView({ behavior: 'smooth' })
  }, [categoryList])

  const updatePositions = () => {
    const position = {}
    if (openTab === 'summary') {
      Object.keys(localeCategoryInfoContentDict).forEach((e) => {
        position[e] = getPosition(circleRefsSummary.current[e])
      })
    } else {
      categoryList.forEach((e) => {
        position[e + '-search'] = getPosition(circleRefsSearch.current[e])
      })
    }
    setCirclePositions(position)
  }

  useLayoutEffect(() => {
    const resizeListener = () => setResize((prevValue) => prevValue + 1)
    window.addEventListener('resize', resizeListener)
    return () => window.removeEventListener('resize', resizeListener)
  }, [])

  useEffect(() => {
    updatePositions()
  }, [categoryList, openTab])

  useEffect(() => {
    if (
      Object.values(circleRefsSummary.current).filter(
        (value) => value.offsetWidth > 0
      ).length &&
      openTab === 'summary' &&
      !summaryCirclesVisible
    ) {
      updatePositions()
      setSummaryCirclesVisible(true)
    } else if (
      Object.values(circleRefsSummary.current).filter(
        (value) => value.offsetWidth > 0
      ).length === 0 &&
      openTab === 'summary'
    ) {
      setSummaryCirclesVisible(false)
    }
  }, [resize])

  return (
    <SectionWrapper {...rest}>
      <Container>
        {einleitung?.texts[0]?.content && (
          <>
            <StyledText
              dangerouslySetInnerHTML={{
                __html: einleitung.texts[0].content
              }}
            />
            <br />
          </>
        )}
        <TabsWrapper>
          <Tabs
            className="nav-fill"
            defaultActiveKey="summary"
            onSelect={(e) => {
              setOpenTab(e)
              e === 'part'
                ? handleSetSection(categorySearchTab)
                : handleSetSection(categorySummaryTab)
            }}
          >
            <Tab
              style={{ height: '100%' }}
              eventKey="summary"
              title={einleitung?.texts[2]?.content}
            >
              {einleitung?.texts[1]?.content && (
                <>
                  <StyledText2
                    dangerouslySetInnerHTML={{
                      __html: einleitung.texts[1].content
                    }}
                  />
                  <br />
                  <br />
                </>
              )}
              <SVGWrapper>
                <ProduktKlassifizierung
                  onSetSection={handleSetSection}
                  onSetCategory={setCategorySummaryTab}
                  lang={locale}
                  selectedCategory={categorySummaryTab}
                />
              </SVGWrapper>
              <SummaryCircleWrapper>
                <StyledTitle color="rgb(32.156372%,47.058105%,63.922119%)">
                  {localeTexts.mobileAnlageprodukteKapitalschutz}
                </StyledTitle>
                <CircleWrapper style={{ marginBottom: '20px' }}>
                  {capitalProtect.map((e) => {
                    return (
                      <Circle
                        className={
                          e === categorySummaryTab ? 'circle-color-fix' : ''
                        }
                        onClick={() => {
                          handleSetSection(e)
                          setCategorySummaryTab(e)
                        }}
                        background={categoryInfoColorDict[e]}
                        animation={circlePositions[e]}
                        key={e}
                        ref={(el) => (circleRefsSummary.current[e] = el)}
                      >
                        {localeCategoryInfoContentDict[e]}
                      </Circle>
                    )
                  })}
                </CircleWrapper>
                <StyledTitle color="rgb(0%,52.941895%,49.01886%)">
                  {localeTexts.mobileAnlageprodukteOhneKapitalschutz}
                </StyledTitle>
                <CircleWrapper style={{ marginBottom: '30px' }}>
                  {investProductsWithoutCP.map((e) => {
                    return (
                      <Circle
                        className={
                          e === categorySummaryTab ? 'circle-color-fix' : ''
                        }
                        onClick={() => {
                          handleSetSection(e)
                          setCategorySummaryTab(e)
                        }}
                        background={categoryInfoColorDict[e]}
                        animation={circlePositions[e]}
                        key={e}
                        ref={(el) => (circleRefsSummary.current[e] = el)}
                      >
                        {localeCategoryInfoContentDict[e]}
                      </Circle>
                    )
                  })}
                </CircleWrapper>
                <StyledTitle color="rgb(41.175842%,25.097656%,39.99939%)">
                  {localeTexts.mobileHebelprodukte}
                </StyledTitle>
                <CircleWrapper>
                  {leverageProducts.map((e) => {
                    return (
                      <Circle
                        className={
                          e === categorySummaryTab ? 'circle-color-fix' : ''
                        }
                        onClick={() => {
                          handleSetSection(e)
                          setCategorySummaryTab(e)
                        }}
                        background={categoryInfoColorDict[e]}
                        animation={circlePositions[e]}
                        key={e}
                        ref={(el) => (circleRefsSummary.current[e] = el)}
                      >
                        {localeCategoryInfoContentDict[e]}
                      </Circle>
                    )
                  })}
                </CircleWrapper>
              </SummaryCircleWrapper>
              {data?.section_title && (
                <SectionBody ref={refSummary}>
                  {data?.section_title && (
                    <SectionTitle
                      mb={['45px', null, null, '70px']}
                      {...data.section_title}
                    />
                  )}
                  {data?.texts?.[0] && (
                    <StyledText
                      dangerouslySetInnerHTML={{
                        __html: data.texts[0].content
                      }}
                    />
                  )}

                  {data?.items?.[0] &&
                    data.items.map((item, i) => {
                      const isEven = i % 2 === 0
                      return (
                        <ClassificationItem
                          key={item.id}
                          isEven={!isEven}
                          color={data.title}
                        >
                          <Row>
                            <Col lg={6}>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: item.title
                                }}
                              />
                            </Col>
                            <Col lg={6}>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: item.description
                                }}
                              />
                            </Col>
                          </Row>
                        </ClassificationItem>
                      )
                    })}
                </SectionBody>
              )}
            </Tab>
            <Tab eventKey="part" title={einleitung?.texts[3]?.content}>
              <CheckWrapper>
                <Form>
                  {search.items[0]?.texts.map((e, i) => (
                    <Form.Check // prettier-ignore
                      type="checkbox"
                      name="category"
                      key={`check-${i}`}
                      id={`check-${i}`}
                      label={`${e.content}`}
                    />
                  ))}
                  {search.buttons?.length > 0 && (
                    <button
                      onClick={(event) => {
                        event.preventDefault()
                        handleSetSection(null)
                        setCategorySearchTab(null)
                        if (document.querySelector('[name=category]:checked')) {
                          // onSetCurrentPoll(false)
                          const checkedArray = document.querySelectorAll(
                            '[name=category]:checked'
                          )
                          let categoryList = [
                            ...checkCategoriesDict[checkedArray[0].id]
                          ]
                          if (checkedArray.length > 1) {
                            Array.from(checkedArray)
                              .slice(1, checkedArray.length)
                              .forEach((e) => {
                                categoryList = intersect(
                                  checkCategoriesDict[e.id],
                                  categoryList
                                )
                              })
                          }
                          setCategoryList(categoryList)
                          setCategoryListKeyAddition((prev) => prev + 1)
                        } else {
                          setCategoryList([])
                        }
                      }}
                      type="submit"
                      className="btn btn-outline-primary"
                    >
                      {search.buttons[0].content}
                    </button>
                  )}
                </Form>
                <div
                  style={{ display: 'block', marginTop: 20, color: 'grey' }}
                  dangerouslySetInnerHTML={{
                    __html: search?.texts[0].content
                  }}
                />
              </CheckWrapper>
              {categoryList.length === 0 && categoryListKeyAddition > 0 && (
                <>
                  <StyledText2
                    ref={refSearch2}
                    dangerouslySetInnerHTML={{
                      __html: search?.texts[1]?.content
                    }}
                  />
                  <br />
                  <br />
                </>
              )}
              {categoryList.length > 0 && einleitung?.texts[1]?.content && (
                <>
                  <StyledText2
                    ref={refSearch2}
                    dangerouslySetInnerHTML={{
                      __html: einleitung.texts[1].content
                    }}
                  />
                  <br />
                  <br />
                </>
              )}
              <CircleWrapper>
                {categoryList.map((e) => {
                  return (
                    <Circle
                      className={
                        e === categorySearchTab ? 'circle-color-fix' : ''
                      }
                      onClick={() => {
                        handleSetSection(e)
                        setCategorySearchTab(e)
                      }}
                      background={categoryInfoColorDict[e]}
                      animation={circlePositions[e + '-search']}
                      key={e + categoryListKeyAddition.toString()}
                      ref={(el) => (circleRefsSearch.current[e] = el)}
                    >
                      {localeCategoryInfoContentDict[e]}
                    </Circle>
                  )
                })}
              </CircleWrapper>
              {data?.section_title && (
                <SectionBody ref={refSearch}>
                  {data?.section_title && (
                    <SectionTitle
                      mb={['45px', null, null, '70px']}
                      {...data.section_title}
                    />
                  )}
                  {data?.texts?.[0] && (
                    <StyledText
                      dangerouslySetInnerHTML={{
                        __html: data.texts[0].content
                      }}
                    />
                  )}

                  {data?.items?.[0] &&
                    data.items.map((item, i) => {
                      const isEven = i % 2 === 0
                      return (
                        <ClassificationItem
                          key={item.id}
                          isEven={!isEven}
                          color={data.title}
                        >
                          <Row>
                            <Col lg={6}>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: item.title
                                }}
                              />
                            </Col>
                            <Col lg={6}>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: item.description
                                }}
                              />
                            </Col>
                          </Row>
                        </ClassificationItem>
                      )
                    })}
                </SectionBody>
              )}
            </Tab>
          </Tabs>
        </TabsWrapper>
        {einleitung?.items?.map((item) => (
          <BoxImage
            key={item.id}
            title={item.title}
            showTitle={false}
            desc={item.description}
            image={item.images[0]}
            path={item.pdfs[0]?.src.localFile.publicURL || item.path}
          />
        ))}
      </Container>
    </SectionWrapper>
  )
}

export default BSWProduktklassifizierungArea
